import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, throwError } from 'rxjs';
import { SKIP_GLOBAL_GET_LOADING_UI } from './http-context-tokens';
import { AuthService } from './services/auth-service.service';
import { SpinnerService } from './services/spinner.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const spinner = inject(SpinnerService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const token = sessionStorage.getItem('token');
  const skipSpinner = req.context.get(SKIP_GLOBAL_GET_LOADING_UI);

    const isExternalPublicApi = req.url.includes('viacep.com.br');

  if (token && !isExternalPublicApi) {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };

    // Local aponta direto ao Core (sem gateway). O Core lê X-User-Data para
    // AuthenticatedUserService (ex.: canceled_by_user_id / "Solicitado por").
    //
    // Importante: XMLHttpRequest só aceita header values em ISO-8859-1.
    // JSON com acentos (nome da empresa/usuário) gera status 0 Unknown Error no browser.
    // Enviar só ids + códigos ASCII; o Core resolve o nome pelo user id.
    const user = authService.getUser();
    if (user?.id != null) {
      try {
        headers['X-User-Data'] = JSON.stringify({
          id: user.id,
          email: String(user.email ?? '').replace(/[^\x00-\x7F]/g, ''),
          company: {
            id: user.company?.id ?? 0,
            name: String(user.company?.name ?? 'Mile')
              .replace(/[^\x00-\x7F]/g, '')
              .slice(0, 80) || 'Mile',
            code: String(user.company?.code ?? '').replace(/[^\x00-\x7F]/g, ''),
          },
        });
        if (user.role?.name) {
          headers['X-User-Permission'] = JSON.stringify({
            name: String(user.role.name).replace(/[^\x00-\x7F]/g, ''),
            permissions: Array.isArray(user.role.permissions)
              ? user.role.permissions
                  .map((p: { name?: string }) => ({
                    name: String(p?.name ?? '').replace(/[^\x00-\x7F]/g, ''),
                  }))
                  .filter((p: { name: string }) => !!p.name)
                  .slice(0, 200)
              : [],
          });
        }
      } catch (e) {
        console.warn('⚠️ [authInterceptor] Falha ao montar X-User-Data:', e);
      }
    }

    req = req.clone({ setHeaders: headers });
  }

  if (!skipSpinner) {
    spinner.show();
  }

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      // Login inválido também retorna 401 — não desloga/redireciona nesse caso
      const isLoginRequest = req.url.includes('/oauth/token');

      if (err.status === 401 && !isLoginRequest) {
        authService.clearSessionStorage();
        void router.navigate(['/login']);
      }

      const errorObj = {
        success: false,
        message: err.message,
        status: err.status,
        error: err.error,
        url: err.url,
      };

      return throwError(() => errorObj);
    }),
    finalize(() => {
      if (!skipSpinner) {
        spinner.hide();
      }
    })
  );
};
